"use client";

import React from "react";
import { useClientValue } from "@/hooks/useClientValue";
import { createMachine } from "xstate";
import { useMachine } from "@xstate/react";
import { type ErrorLike } from "@apollo/client";
import { useLazyQuery } from "@apollo/client/react";
import LoadingPlaceholder from "../LoadingPlaceholder";

import { queries } from "@/api";
import { graphQLErrorsFrom } from "@/api/graphQLErrorsFrom";

import { useSession } from "@/contexts/session";

import { useFeatureFlagsQuery } from "@/components/utilities/featureFlag";

import useRefreshCacheOnExternalSignIn from "./useRefreshCacheOnExternalSignIn";
import useCreateSessionFromToken, {
	useLongLivedTokenParam,
} from "./useCreateSessionFromToken";

const initializeAppStateMachine = createMachine({
	id: "app-initialization",
	initial: "idle",
	states: {
		idle: {
			always: [
				{
					target: "longLivedTokenUnverified",
					cond: "hasLongLivedToken",
				},
				{
					target: "sessionUnverified",
					cond: "hasValidSession",
				},
				{
					target: "sessionInvalid",
				},
			],
		},
		initialized: {},
		longLivedTokenUnverified: {
			entry: "createSessionFromToken",
			on: {
				VALIDATE_LONG_LIVED_TOKEN: {
					target: "initialized",
				},
				INVALIDATE_LONG_LIVED_TOKEN: [
					{
						target: "sessionUnverified",
						cond: "hasValidSession",
					},
					{
						target: "sessionInvalid",
					},
				],
			},
		},
		sessionInvalid: {
			entry: "removeSession",
			always: {
				target: "initialized",
			},
		},
		sessionUnverified: {
			entry: "fetchInitializeApp",
			on: {
				VALIDATE_SESSION: {
					target: "initialized",
				},
				INVALIDATE_SESSION: {
					target: "sessionInvalid",
				},
			},
		},
	},
});

const InitializeApp = ({ children }: { children: React.ReactNode }) => {
	const isClient = useClientValue(
		() => true,
		() => false,
	);
	// LLT query param will be set if the user signed in from an external link
	const [longLivedToken, setLongLivedTokenParam] = useLongLivedTokenParam();

	const { hasAuthenticatedSession, hasSessionExpired, removeSession } =
		useSession();

	const [createSessionFromToken] = useCreateSessionFromToken(longLivedToken, {
		onCompleted: () => {
			setLongLivedTokenParam(null);
			send("VALIDATE_LONG_LIVED_TOKEN");
		},
		onError: () => {
			setLongLivedTokenParam(null);
			send("INVALIDATE_LONG_LIVED_TOKEN");
		},
	});

	const [executeInitializeApp] = useLazyQuery(queries.INITIALIZE_APP, {
		/**
		 * For the initial query, we ensure consistency with the server.
		 * Subsequent queries may use cached values.
		 */
		fetchPolicy: "network-only",
	});

	/*
	 * Apollo Client 4 removed `onCompleted` and `onError` from lazy queries; the
	 * execute function resolves with the result and rejects on an error, so the
	 * session is validated from the promise instead.
	 */
	const fetchInitializeApp = () =>
		executeInitializeApp().then(
			() => {
				send("VALIDATE_SESSION");
			},
			(error: ErrorLike) => {
				const isTokenRejected = graphQLErrorsFrom(error).some(
					({ message }) => message === "ErrorTokenInvalid",
				);
				send(isTokenRejected ? "INVALIDATE_SESSION" : "VALIDATE_SESSION");
			},
		);

	const [current, send] = useMachine(initializeAppStateMachine, {
		guards: {
			hasValidSession: () => hasAuthenticatedSession && !hasSessionExpired,
			hasLongLivedToken: () => Boolean(longLivedToken),
		},
		actions: {
			createSessionFromToken,
			removeSession,
			fetchInitializeApp,
		},
	});

	/*
	 * Fetch features regardless of the state of the user's session (existence
	 * and/or validity)
	 */
	const { refetch: fetchFeatures } = useFeatureFlagsQuery();

	useRefreshCacheOnExternalSignIn({
		shouldRefresh: current.matches("initialized"),
		refreshCache() {
			fetchInitializeApp();
			fetchFeatures();
		},
	});

	if (!isClient) {
		return null;
	}

	if (
		current.matches("idle") ||
		current.matches("sessionUnverified") ||
		current.matches("longLivedTokenUnverified")
	) {
		return <LoadingPlaceholder />;
	}

	return <>{children}</>;
};

export default InitializeApp;
